import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import { Button, cn } from "@nexus/ui";
import {
  ClientContextErrorCodes,
  NEW_PARTITION_RULE,
  ONBOARDING_EDIT_COLUMNS,
  hasClientIndustrySpaces,
  isNewCompPartition,
  validateNewPartition,
  type OnboardingEditColumn,
  type TenantOnboardingReadModel,
} from "@nexus/contracts";
import { onboardingAvailabilityQueryOptions, tenantOnboardingQueryOptions } from "@/lib/queries";
import { ApiError } from "@/lib/api-client";
import { TenantOnboardingJobError } from "@/features/identity-hub/api/identity-hub.api";
import { useEditTenantOnboarding, useInitializeTenantOnboarding } from "@/features/identity-hub/hooks/useIdentityHub";
import {
  buildOnboardingCreateFields,
  changedOnboardingColumns,
  createOnboardingDraft,
  isOnboardingDraftValueEmpty,
  onboardingDraftFromValues,
  parseOnboardingDraft,
  type OnboardingDraft,
  type OnboardingDraftValue,
} from "@/features/identity-hub/lib/client-onboarding";
import {
  createCustomerSetupDraft,
  parseCustomerSetupDraft,
  type CustomerSetupDraft,
} from "@/features/identity-hub/lib/customer-setup";
import {
  onboardingSaveErrorBanner,
  toOnboardingSaveError,
  type OnboardingSaveError,
} from "@/features/identity-hub/lib/onboarding-save-error";
import { ClientOnboardingFields, onboardingFieldLabel } from "./ClientOnboardingFields";
import { CustomerSetupFields } from "./CustomerSetupFields";
import { CustomerSetupReadOnly } from "./CustomerSetupReadOnly";
import { ONBOARDING_FULL_ROW_CLASS, OnboardingSection } from "./OnboardingSection";

export interface OrgOnboardingFormApi {
  isDirty: boolean;
  canSave: boolean;
  isSaving: boolean;
  handleSave: () => Promise<boolean>;
}

interface OrgDrawerOnboardingTabProps {
  tenantId: string;
  /** Archived orgs and the platform org are read-only. Every org is read-only while onboarding writes are off. */
  locked: boolean;
  onFormApiChange?: (api: OrgOnboardingFormApi) => void;
}

const INPUT_CLASS =
  "h-9 w-full rounded-md border bg-background px-3 text-[13px] text-foreground outline-none placeholder:text-muted-foreground focus:border-ring focus:ring-1 focus:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50";

/** A new onboarding starts from the create defaults wherever nothing is stored yet. */
function withCreateDefaults(stored: OnboardingDraft): OnboardingDraft {
  const defaults = createOnboardingDraft();
  const filled = Object.entries(stored).filter(([, value]) => !isOnboardingDraftValueEmpty(value));
  return { ...stored, ...defaults, ...Object.fromEntries(filled) };
}

/**
 * Edit Organization → Client onboarding. Reads the Nexus onboarding record;
 * every save goes through the client-context API and the record changes only
 * after the API accepts it, so a rejected save keeps the draft. The customer
 * setup shown is Nexus's saved snapshot — read-only, never fetched from
 * BigQuery — and can only be sent with the onboarding create of an
 * organization that has neither a record nor a snapshot yet. The tab follows
 * IDENTITY_HUB_UPDATE_TENANT on the organization; an archived organization
 * is `locked`, and so is every organization while the deployment has onboarding writes off. Comp
 * industries and every other tenant field keep their existing tabs and save path.
 */
export function OrgDrawerOnboardingTab({
  tenantId,
  locked,
  onFormApiChange,
}: OrgDrawerOnboardingTabProps) {
  const { t } = useTranslation();
  const query = useQuery(tenantOnboardingQueryOptions(tenantId));
  const availabilityQuery = useQuery(onboardingAvailabilityQueryOptions());
  const isReadOnly = locked || availabilityQuery.data?.writesEnabled !== true;
  /** Archived organizations are locked for their own reason; this is only the deployment switch. */
  const isWritesOffNoticeShown = !locked && availabilityQuery.data?.writesEnabled === false;
  const initializeMut = useInitializeTenantOnboarding();
  const initializeIdempotencyKey = useRef<string | null>(null);
  useEffect(() => {
    initializeIdempotencyKey.current = null;
  }, [tenantId]);
  const editMut = useEditTenantOnboarding();

  const [draft, setDraft] = useState<OnboardingDraft>({});
  const [initialDraft, setInitialDraft] = useState<OnboardingDraft>({});
  const [clientIndustry, setClientIndustry] = useState("");
  const [isIndustryTouched, setIsIndustryTouched] = useState(false);
  const [setupDraft, setSetupDraft] = useState<CustomerSetupDraft>(createCustomerSetupDraft);
  const [isSetupSubmitted, setIsSetupSubmitted] = useState(false);
  const [saveError, setSaveError] = useState<OnboardingSaveError | null>(null);
  const [compKey, setCompKey] = useState("");
  const [initialCompKey, setInitialCompKey] = useState("");

  const readModel = query.data;
  const isMissing = readModel?.exists === false;
  const isCreateMode = isMissing && !isReadOnly;
  const isSetupMissing = readModel?.setup.exists === false;
  const changedColumns = useMemo(() => changedOnboardingColumns(draft, initialDraft), [draft, initialDraft]);
  const isCompDirty = compKey.trim() !== initialCompKey.trim();
  // Only a changed key that names a new competitor partition is checked; the saved one stays as it is.
  const isCompInvalid =
    isCompDirty &&
    !!readModel &&
    isNewCompPartition(compKey, readModel.customerName) &&
    validateNewPartition(compKey, "compKey") !== null;
  const isDirty = changedColumns.length > 0 || isCompDirty || (isCreateMode && clientIndustry.trim().length > 0);

  const seededCustomerRef = useRef<string | null>(null);
  useEffect(() => {
    if (!readModel) return;
    const isNewCustomer = seededCustomerRef.current !== readModel.customerName;
    if (isDirty && !isNewCustomer) return;
    seededCustomerRef.current = readModel.customerName;
    const stored = onboardingDraftFromValues(readModel.values);
    const seeded = readModel.exists ? stored : withCreateDefaults(stored);
    const seededComp = readModel.compKey || readModel.customerName;
    setDraft(seeded);
    setInitialDraft(seeded);
    setCompKey(seededComp);
    setInitialCompKey(seededComp);
    if (isNewCustomer) {
      setClientIndustry("");
      setIsIndustryTouched(false);
      setSetupDraft(createCustomerSetupDraft());
      setIsSetupSubmitted(false);
      setSaveError(null);
    }
    // Reseed only when a fresh read arrives; local edits survive unless the customer itself changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readModel]);

  const { errors } = useMemo(() => parseOnboardingDraft(draft, changedColumns), [draft, changedColumns]);
  const hasFieldErrors = Object.keys(errors).length > 0;
  const isIndustryMissing = isCreateMode && clientIndustry.trim().length === 0;
  const hasIndustrySpaces = isCreateMode && hasClientIndustrySpaces(clientIndustry);
  const parsedSetup = useMemo(() => parseCustomerSetupDraft(setupDraft), [setupDraft]);
  const isSetupInvalid = isCreateMode && isSetupMissing && parsedSetup.setup === null;
  const isSaving = initializeMut.isPending || editMut.isPending;
  const savePhase = initializeMut.isPending
    ? initializeMut.savePhase
    : editMut.isPending
      ? editMut.savePhase
      : initializeMut.savePhase ?? editMut.savePhase;
  const canSave =
    !isReadOnly &&
    !!readModel &&
    isDirty &&
    !hasFieldErrors &&
    !isIndustryMissing &&
    !hasIndustrySpaces &&
    !isSetupInvalid &&
    compKey.trim().length > 0 &&
    !isCompInvalid &&
    !isSaving;

  const handleFieldChange = (column: OnboardingEditColumn, value: OnboardingDraftValue) => {
    setDraft((current) => ({ ...current, [column]: value }));
  };

  const handleSave = async (): Promise<boolean> => {
    setIsIndustryTouched(true);
    if (isCreateMode) setIsSetupSubmitted(true);
    if (!canSave) return false;
    setSaveError(null);
    try {
      if (isCreateMode) {
        const { values } = buildOnboardingCreateFields(draft, clientIndustry);
        const setup = isSetupMissing ? (parsedSetup.setup ?? undefined) : undefined;
        const idempotencyKey = (initializeIdempotencyKey.current ??= crypto.randomUUID());
        await initializeMut.mutateAsync({ tenantId, fields: values, setup, compKey: compKey.trim(), idempotencyKey });
        initializeIdempotencyKey.current = null;
        setClientIndustry("");
        setIsIndustryTouched(false);
        setSetupDraft(createCustomerSetupDraft());
        setIsSetupSubmitted(false);
      } else {
        const { values } = parseOnboardingDraft(draft, changedColumns);
        await editMut.mutateAsync({
          tenantId,
          changes: values,
          ...(isCompDirty ? { compKey: compKey.trim() } : {}),
        });
      }
      setInitialDraft(draft);
      setInitialCompKey(compKey.trim());
      return true;
    } catch (err) {
      setSaveError(toOnboardingSaveError(err, t("iam.onboarding.errors.saveFailed", "Client onboarding was not saved. Try again."), t));
      return false;
    }
  };

  const handleDiscard = () => {
    setDraft(initialDraft);
    setCompKey(initialCompKey);
    setClientIndustry("");
    setIsIndustryTouched(false);
    setSetupDraft(createCustomerSetupDraft());
    setIsSetupSubmitted(false);
    setSaveError(null);
  };

  const onFormApiChangeRef = useRef(onFormApiChange);
  onFormApiChangeRef.current = onFormApiChange;
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;
  useEffect(() => {
    onFormApiChangeRef.current?.({ isDirty, canSave, isSaving, handleSave: () => handleSaveRef.current() });
  }, [isDirty, canSave, isSaving]);

  if (!readModel && !query.isError) {
    return (
      <p className="px-5 py-6 text-xs text-muted-foreground" data-testid="org-onboarding-loading">
        {t("iam.onboarding.loading", "Loading client onboarding…")}
      </p>
    );
  }

  if (!readModel) {
    return (
      <OnboardingLoadError error={query.error} onRetry={() => void query.refetch()} isRetrying={query.isFetching} />
    );
  }

  const inlineErrorFields: readonly string[] = isCreateMode
    ? ["industry", "compKey", ...ONBOARDING_EDIT_COLUMNS]
    : ["compKey", ...ONBOARDING_EDIT_COLUMNS];
  const saveErrorBanner = onboardingSaveErrorBanner(saveError, inlineErrorFields);

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="org-onboarding-tab">
      <div className="min-h-0 flex-1 overflow-y-auto">
        {isWritesOffNoticeShown && (
          <p
            className="mx-8 mt-6 rounded-lg border border-border-subtle bg-muted/30 px-4 py-3 text-xs text-muted-foreground"
            role="status"
            data-testid="org-onboarding-writes-off"
          >
            {t(
              "iam.onboarding.writesOff",
              "Editing is turned off on this deployment. You can view the saved onboarding, but changes can't be saved yet.",
            )}
          </p>
        )}
        {saveErrorBanner && (
          <p
            className="mx-8 mt-6 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-foreground"
            role="alert"
            data-testid="org-onboarding-save-error"
          >
            {saveErrorBanner}
          </p>
        )}
        <OnboardingIdentity
          readModel={readModel}
          isCreateMode={isCreateMode}
          isMissing={isMissing}
          clientIndustry={clientIndustry}
          onClientIndustryChange={setClientIndustry}
          industryError={
            hasIndustrySpaces
              ? t("iam.onboarding.industrySpaces", "Do not use spaces")
              : isIndustryTouched && isIndustryMissing
                ? t("iam.onboarding.industryRequired", "Client industry is required to create onboarding")
                : saveError?.field === "industry"
                  ? saveError.message
                  : null
          }
          onIndustryBlur={() => setIsIndustryTouched(true)}
          compKey={compKey}
          onCompKeyChange={setCompKey}
          compError={
            isCompInvalid
              ? t("iam.onboarding.errors.partitionInvalid", NEW_PARTITION_RULE)
              : saveError?.field === "compKey"
                ? saveError.message
                : null
          }
          disabled={isReadOnly}
        />
        {readModel.setup.exists ? (
          <CustomerSetupReadOnly values={readModel.setup.values} />
        ) : isCreateMode ? (
          <div data-testid="customer-setup-missing">
            <CustomerSetupFields
              draft={setupDraft}
              errors={isSetupSubmitted ? parsedSetup.errors : {}}
              onChange={setSetupDraft}
              disabled={isReadOnly}
            />
          </div>
        ) : (
          <p
            className="mx-8 my-6 rounded-lg border border-border-subtle bg-muted/30 px-4 py-3 text-xs text-muted-foreground"
            data-testid="customer-setup-missing"
          >
            {t(
              "iam.onboarding.setup.missingReadOnly",
              "Customer setup snapshot is not available in Nexus.",
            )}
          </p>
        )}
        {!isMissing || isCreateMode ? (
          <ClientOnboardingFields
            draft={draft}
            errors={errors}
            saveError={saveError}
            onChange={handleFieldChange}
            disabled={isReadOnly}
          />
        ) : null}
      </div>
      {!isReadOnly && (
        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border px-5 py-3">
          {isDirty && (
            <Button size="sm" variant="ghost" disabled={isSaving} onClick={handleDiscard}>
              {t("iam.orgDrawer.settings.discard", "Discard")}
            </Button>
          )}
          <Button
            size="sm"
            iconStart="check"
            data-testid="org-onboarding-save"
            disabled={!canSave}
            onClick={() => void handleSave()}
          >
            {isSaving
              ? savePhase === "verifying"
                ? t("iam.onboarding.verifying", "Verifying…")
                : t("iam.orgDrawer.settings.saving", "Saving…")
              : !isDirty && savePhase === "saved"
                ? t("iam.onboarding.saved", "Saved")
                : isCreateMode
                  ? t("iam.onboarding.create", "Create onboarding")
                  : t("iam.onboarding.save", "Save onboarding")}
          </Button>
        </div>
      )}
    </div>
  );
}

interface OnboardingIdentityProps {
  readModel: TenantOnboardingReadModel;
  isCreateMode: boolean;
  /** Nexus holds no onboarding record for this customer. */
  isMissing: boolean;
  clientIndustry: string;
  onClientIndustryChange: (value: string) => void;
  industryError: string | null;
  onIndustryBlur: () => void;
  compKey: string;
  onCompKeyChange: (value: string) => void;
  compError: string | null;
  disabled: boolean;
}

/** Customer key and Client Industry: both locked once the row exists. */
function OnboardingIdentity({
  readModel,
  isCreateMode,
  isMissing,
  clientIndustry,
  onClientIndustryChange,
  industryError,
  onIndustryBlur,
  compKey,
  onCompKeyChange,
  compError,
  disabled,
}: OnboardingIdentityProps) {
  const { t } = useTranslation();
  const savedIndustry = typeof readModel.values.industry === "string" ? readModel.values.industry : null;

  return (
    <OnboardingSection
      data-testid="org-onboarding-identity"
      title={t("iam.onboarding.identityTitle", "Customer")}
      hint={t("iam.onboarding.identityHint", "The customer this onboarding belongs to.")}
    >
      {isMissing && (
        <p className={cn("text-xs text-muted-foreground", ONBOARDING_FULL_ROW_CLASS)} data-testid="org-onboarding-empty">
          {isCreateMode
            ? t(
                "iam.onboarding.emptyState",
                "No onboarding is saved for this customer yet. Fill it in and create it. The data setup is included when none is saved yet.",
              )
            : t(
                "iam.onboarding.notSetUpInNexus",
                "Client onboarding is not set up in Nexus for this customer. Onboarding is read-only here, so it can't be created from Nexus yet.",
              )}
        </p>
      )}
      <ReadOnlyValue
        label={t("iam.onboarding.customerLabel", "Customer")}
        hint={t(
          "iam.onboarding.customerHint",
          "From Customer partition on the Organization tab. It links this organization to its customer data, so it can't be edited here.",
        )}
        value={readModel.customerName}
      />
      <div className="flex min-w-0 flex-col gap-1.5">
        <label htmlFor="org-onboarding-comp-key" className="text-xs font-medium text-foreground">
          {t("iam.onboarding.compKeyLabel", "Competitor partition")}
        </label>
        <input
          id="org-onboarding-comp-key"
          type="text"
          value={compKey}
          onChange={(e) => onCompKeyChange(e.target.value)}
          disabled={disabled}
          aria-invalid={compError ? true : undefined}
          className={cn(INPUT_CLASS, compError ? "border-destructive" : "border-input-border")}
        />
        <p className={cn("text-[11px]", compError ? "text-destructive" : "text-muted-foreground")}>
          {compError ??
            t(
              "iam.onboarding.compKeyHint",
              "Defaults to the customer key when no mapping is saved.",
            )}
        </p>
      </div>
      {isCreateMode ? (
        <div className="flex min-w-0 flex-col gap-1.5">
          <label htmlFor="org-onboarding-client-industry" className="text-xs font-medium text-foreground">
            {onboardingFieldLabel(t, "industry")} <span className="text-destructive">*</span>
          </label>
          <input
            id="org-onboarding-client-industry"
            type="text"
            value={clientIndustry}
            onChange={(e) => onClientIndustryChange(e.target.value)}
            onBlur={onIndustryBlur}
            disabled={disabled}
            aria-invalid={industryError ? true : undefined}
            className={cn(INPUT_CLASS, industryError ? "border-destructive" : "border-input-border")}
          />
          <p className={cn("text-[11px]", industryError ? "text-destructive" : "text-muted-foreground")}>
            {industryError ??
              t("iam.onboarding.industryCreateHint", "One word, no spaces. Can't be changed later.")}
          </p>
        </div>
      ) : (
        <ReadOnlyValue
          label={onboardingFieldLabel(t, "industry")}
          hint={t(
            "iam.onboarding.industryReadOnlyHint",
            "Set when onboarding was created. It is also saved in the data setup, which can't change, so it stays locked to keep the two the same. Separate from the Comp industries.",
          )}
          value={savedIndustry ?? t("iam.shared.none", "None")}
          testId="org-onboarding-client-industry"
        />
      )}
    </OnboardingSection>
  );
}

function ReadOnlyValue({ label, hint, value, testId }: { label: string; hint: string; value: string; testId?: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="flex items-center gap-1.5 text-xs font-medium text-foreground">
        <Lock className="size-3 text-muted-foreground" aria-hidden />
        {label}
      </span>
      <span className="font-mono text-xs text-foreground" data-testid={testId}>
        {value}
      </span>
      <span className="text-[11px] text-muted-foreground">{hint}</span>
    </div>
  );
}

function OnboardingLoadError({
  error,
  onRetry,
  isRetrying,
}: {
  error: Error;
  onRetry: () => void;
  isRetrying: boolean;
}) {
  const { t } = useTranslation();
  const code = error instanceof ApiError || error instanceof TenantOnboardingJobError ? error.code : undefined;
  const message =
    code === ClientContextErrorCodes.CUSTOMER_NOT_CONFIGURED
      ? t(
          "iam.onboarding.errors.customerNotConfigured",
          "Set the Customer partition on the Organization tab before adding client onboarding.",
        )
      : code === ClientContextErrorCodes.MULTIPLE_CUSTOMER_NAMES
        ? t(
            "iam.onboarding.errors.multipleCustomers",
            "Customer partition lists more than one BigQuery customer. Client onboarding needs exactly one.",
          )
        : t("iam.onboarding.errors.loadFailed", "Client onboarding could not be loaded.");
  const isRetryable =
    code !== ClientContextErrorCodes.CUSTOMER_NOT_CONFIGURED && code !== ClientContextErrorCodes.MULTIPLE_CUSTOMER_NAMES;

  return (
    <div className="m-5 flex flex-col items-start gap-3 rounded-lg border border-warning-border bg-warning-subtle px-4 py-3" role="alert">
      <p className="text-sm text-warning-subtle-foreground">{message}</p>
      {isRetryable && (
        <Button size="sm" variant="outline" iconStart="refresh-cw" disabled={isRetrying} onClick={onRetry}>
          {t("iam.onboarding.retryLoad", "Try again")}
        </Button>
      )}
    </div>
  );
}
